import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useStore } from '../store/store';
import { LuppApi } from '../api/services/lupp/lupp.service';
import { LUPP_API_BASE_PATH } from '../api/services/lupp/constants.lupp';
import { resolveLabelsLocale } from '../utils/tools';

const CONTENTFUL_GRAPHQL_URL = import.meta.env.VITE_CONTENTFUL_GRAPHQL_URL;
const CONTENTFUL_ACCESS_TOKEN = import.meta.env.VITE_CONTENTFUL_ACCESS_TOKEN;

interface ContentfulResponse {
  data: {
    jsonContent: {
      sys: {
        id: string;
      };
      id: string;
      json: Record<string, string>;
    };
  };
}

interface UseGetLabelsParams {
  id: string;
  locale?: string;
  /** Override label language (e.g., 'en', 'de', 'fr' - forces specific language labels) */
  language?: string | null;
  /** Country code for locale-specific translations (e.g., 'NL' for Netherlands) */
  countryCode?: string;
  enabled?: boolean;
}

/**
 * Map language code to Contentful locale
 * e.g., 'en' -> 'en-US', 'de' -> 'de-DE', 'fr' -> 'fr-FR'
 * Special handling for NL users: 'en' + 'NL' -> 'en-NL'
 */
export const getContentfulLocaleFromLanguage = (
  languageCode: string,
  countryCode?: string
): string => {
  // Special handling for NL (Netherlands) users with English language
  if (languageCode.toLowerCase() === 'en' && countryCode?.toUpperCase() === 'NL') {
    return 'en-NL';
  }

  const languageToLocale: Record<string, string> = {
    en: 'en-US',
    de: 'de-DE',
    fr: 'fr-FR',
    nl: 'nl-NL',
    nb: 'nb-NO',
    ar: 'ar-SA',
  };
  return languageToLocale[languageCode.toLowerCase()] || 'en-US';
};

/**
 * Updates the authenticated partner user's preferred language on the backend
 * Uses the shared LuppApi instance so auth/correlation headers and the gateway's
 * CORS-enabled `sales/X/v1` path prefix are applied consistently with other endpoints
 * @param language - The language code to persist (e.g., 'en', 'de', 'fr')
 */
export const updatePartnerUserLanguage = async (language: string): Promise<void> => {
  const preferredLanguage = getContentfulLocaleFromLanguage(language);

  await LuppApi.patch(`${LUPP_API_BASE_PATH}/partners/users/preferred-language`, {
    preferredLanguage,
  });
};

/**
 * Custom hook to fetch labels from Contentful using GraphQL API
 * Falls back to US English labels if the requested locale is not available
 *
 * @param id - The Contentful entry ID
 * @param locale - The locale to fetch labels for (default: 'en-US')
 * @param language - Override language code (e.g., 'en', 'de', 'fr')
 * @param enabled - Whether the query should run (default: true)
 *
 * @example
 * ```tsx
 * const { data: labels, isLoading, error } = useGetLabels({
 *   id: '60n6cJSqCElDfYLQN5Sczy',
 *   locale: 'de-DE',
 *   language: null, // Use native German labels
 * });
 *
 * // Force English labels even on German locale:
 * const { data: labels } = useGetLabels({
 *   id: '60n6cJSqCElDfYLQN5Sczy',
 *   locale: 'de-DE',
 *   language: 'en', // Force English labels
 * });
 * ```
 */
export const useGetLabels = ({
  id,
  locale = 'en-US',
  language = null,
  countryCode,
  enabled = true,
}: UseGetLabelsParams) => {
  // If language is set, use the corresponding Contentful locale
  // Pass countryCode to support NL users with English language (en-NL)
  const effectiveLocale = resolveLabelsLocale(
    language ? getContentfulLocaleFromLanguage(language, countryCode) : locale
  );

  return useQuery({
    queryKey: ['contentful-labels', id, effectiveLocale],
    queryFn: async () => {
      const fetchLabels = async (targetLocale: string) => {
        const response = await axios.post<ContentfulResponse>(
          CONTENTFUL_GRAPHQL_URL,
          {
            query: `query ($id: String!, $locale: String!) { 
              jsonContent(id: $id, locale: $locale) { 
                sys { id } 
                id 
                json 
              } 
            }`,
            variables: {
              id,
              locale: targetLocale,
            },
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${CONTENTFUL_ACCESS_TOKEN}`,
            },
          }
        );

        return response.data.data.jsonContent;
      };

      try {
        const result = await fetchLabels(effectiveLocale);

        // If the result is null or doesn't have json, fallback to en-US
        if (!result || !result.json) {
          if (effectiveLocale !== 'en-US') {
            console.warn(
              `Labels not available for locale ${effectiveLocale}, falling back to en-US`
            );
            const fallbackResult = await fetchLabels('en-US');
            return fallbackResult?.json || {};
          }
          return {};
        }

        return result.json;
      } catch (error) {
        // If the requested locale fails, fallback to US English
        if (effectiveLocale !== 'en-US') {
          console.warn(
            `Failed to fetch labels for locale ${effectiveLocale}, falling back to en-US`
          );
          try {
            const fallbackResult = await fetchLabels('en-US');
            return fallbackResult?.json || {};
          } catch (fallbackError) {
            console.error('Failed to fetch fallback labels:', fallbackError);
            throw error;
          }
        }
        throw error;
      }
    },
    enabled,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: false, // Disable retry since we handle fallback internally
  });
};

/**
 * Simplified hook parameters for useLabels
 * Automatically uses language from store
 */
interface UseLabelsParams {
  id: string;
  locale?: string;
  /** Override the active store language; null makes the supplied locale authoritative. */
  language?: string | null;
  /** Country code for locale-specific translations (e.g., 'NL' for Netherlands) */
  countryCode?: string;
  enabled?: boolean;
}

/**
 * Convenience hook that wraps useGetLabels with automatic label language detection from store.
 * Pages should use this hook instead of useGetLabels for automatic language toggle support.
 *
 * @param id - The Contentful entry ID
 * @param locale - The locale to fetch labels for (default: 'en-US')
 * @param enabled - Whether the query should run (default: true)
 *
 * @example
 * ```tsx
 * const { data: labels, isLoading } = useLabels({
 *   id: '6dhtpdHCYn1jTZ1cRAXtJR',
 *   locale: convertToContentfulLocale(locale),
 * });
 * ```
 */
export const useLabels = ({
  id,
  locale = 'en-US',
  language: languageOverride,
  countryCode,
  enabled = true,
}: UseLabelsParams) => {
  const storeLanguage = useStore((state) => state.language);
  const language = languageOverride === undefined ? storeLanguage : languageOverride;

  return useGetLabels({
    id,
    locale,
    language,
    countryCode,
    enabled,
  });
};
